/**
 * `POST /api/threads/reports` — a person reports another person's message, a thread, or a
 * person on the people surface (gh #4661) to the WayAI team (App Store guideline 1.2:
 * user-generated content needs a way to report it, and a timely answer from the developer).
 *
 * A report names only what its reporter can see: a message of someone else's in a thread they
 * belong to, a thread they belong to, or a person they share a thread with or whose pair their
 * own index holds (a contact, an incoming inviter, someone they block). The server records who
 * reported what, with what the reporter saw then — the reported message and the ones before it,
 * or a thread's newest messages — and emails the platform admins. Nothing about a report
 * reaches the person it names. A report repeated within a day answers the first: it is
 * recorded and sent once.
 *
 * COMMUNITIES (gh #4872 Decision 9). A person also reports someone else's message in a community,
 * or a person who is or was its member, while they can read it (an active member, or an owner or
 * admin of its org). The WayAI team gets it as every report; the community's moderators and admins
 * get it too, in their inbox (`GET /api/communities/:communityId/reports`), without the
 * reporter's identity — unless it is about a moderator or an owner or admin of the org, which only
 * the WayAI team gets. Both keep the message as the reporter saw it, so a later edit cannot erase
 * it (Decision 28).
 *
 * `reports.ts` is the engineering triage queue, which never receives a person's messages.
 */

import { z } from 'zod';
import { optionalTrimmedText, uuidSchema } from './common';
import { peopleUserIdSchema } from './people-contacts';
import type { PeopleThreadAttachment, PeopleThreadKind, PeopleThreadMessage } from './people-threads';

// =============================================================================
// LIMITS. Code constants: raising one is a code change.
// =============================================================================

/** The longest note a reporter adds, in characters. */
export const PEOPLE_REPORT_DETAILS_MAX_LENGTH = 500;
/**
 * Reports one person may file in 24 hours; past it 429, which depends only on them. Counted in
 * the report store, in the write it bounds; a repeat of a report within the day counts nothing.
 */
export const PEOPLE_REPORTS_PER_DAY = 20;
/** The messages a report carries of what its reporter saw, the reported one included. */
export const PEOPLE_REPORT_CONTEXT_MESSAGES = 5;

// =============================================================================
// REQUEST
// =============================================================================

/** Why a person reports: a closed list the clients label. */
export const peopleReportReasonSchema = z.enum(['spam', 'harassment', 'hate', 'sexual', 'violence', 'other']);

/** The reporter's own words, optional: trimmed, and absent when nothing is left. */
const reportDetailsSchema = optionalTrimmedText(PEOPLE_REPORT_DETAILS_MAX_LENGTH);

const reportFields = { reason: peopleReportReasonSchema, details: reportDetailsSchema };

/**
 * POST /api/threads/reports.
 *
 * - `message`: a message of someone else's in `thread_id`, which the reporter can see.
 * - `thread`: `thread_id` itself — a 1:1, whose other person is the one reported, or a group.
 * - `user`: a person — in `thread_id` when it is given (a 1:1's other person, or a group's
 *   member), else one whose pair the reporter's own index holds (a contact, an incoming
 *   inviter, someone they block).
 * - `community_message`: someone else's message, or forum post or reply, in a room of
 *   `community_id` the reporter can read.
 * - `community_member`: a person who is or was a member of `community_id`, which the reporter can read.
 */
export const peopleReportBody = z.discriminatedUnion('target', [
  z.object({ target: z.literal('message'), thread_id: uuidSchema, message_id: uuidSchema, ...reportFields }).strict(),
  z.object({ target: z.literal('thread'), thread_id: uuidSchema, ...reportFields }).strict(),
  z.object({ target: z.literal('user'), user_id: peopleUserIdSchema, thread_id: uuidSchema.optional(), ...reportFields }).strict(),
  z.object({
    target: z.literal('community_message'),
    community_id: uuidSchema,
    room_id: uuidSchema,
    message_id: uuidSchema,
    ...reportFields,
  }).strict(),
  z.object({ target: z.literal('community_member'), community_id: uuidSchema, user_id: peopleUserIdSchema, ...reportFields }).strict(),
]);

export type PeopleReportReason = z.infer<typeof peopleReportReasonSchema>;
export type PeopleReportTarget = z.infer<typeof peopleReportBody>['target'];
/** A report about a community's message or member (gh #4872). */
export type CommunityReportTarget = Extract<PeopleReportTarget, `community_${string}`>;
/** A report as a client sends it. */
export type PeopleReportBody = z.input<typeof peopleReportBody>;
/** A report as the route reads it. */
export type PeopleReport = z.output<typeof peopleReportBody>;
type WithoutAnswers<B> = B extends unknown ? Omit<B, 'reason' | 'details'> : never;
/** What a report names — its target and ids — before the reporter gives a reason. */
export type PeopleReportSubject = WithoutAnswers<PeopleReportBody>;

// =============================================================================
// RESPONSE
// =============================================================================

/** A reported message's file as a report keeps it: by name and type, never its bytes. */
export type PeopleReportAttachment = Pick<PeopleThreadAttachment, 'file_name' | 'content_type'>;

/**
 * A message as the reporter saw it, by its author's id; its file by name and type, its bytes
 * staying in the thread. A deleted message is never reported. A community's message also names
 * its room, and a forum post its title (`seq` is its room's own counter).
 */
export type PeopleReportEvidenceMessage = Pick<PeopleThreadMessage, 'message_id' | 'seq' | 'author_user_id' | 'body' | 'created_at'> & {
  attachment: PeopleReportAttachment | null;
  room_id?: string;
  title?: string | null;
};

/** A thread as its rows show it to a member: an org-linked 1:1 is a `dm`; `group_name` is a group's alone. */
export interface PeopleReportThread {
  kind: PeopleThreadKind;
  org_id: string | null;
  group_name: string | null;
}

/** A community a report names, as it stood then: its org and name (gh #4872). */
export interface PeopleReportCommunity {
  community_id: string;
  org_id: string;
  name: string;
}

/**
 * What the reporter saw, as a report keeps it: the thread (null for a person reported from their
 * contacts, and for a community's report, which names its `community` instead) and its messages,
 * oldest first — for a person reported in a community, their newest messages there.
 */
export interface PeopleReportEvidence {
  thread: PeopleReportThread | null;
  community?: PeopleReportCommunity;
  messages: PeopleReportEvidenceMessage[];
}

/** POST /api/threads/reports — the report as recorded; a repeat within a day answers the first. */
export interface PeopleReportResponse {
  data: { report_id: string; created_at: string };
}
